import { getApiBaseUrl } from './runtimeUrls';

// Enterprise configuration management
const deriveApiBaseUrl = () => getApiBaseUrl();

export const API_CONFIG = {
  // Production-ready base configuration
  BASE_URL: deriveApiBaseUrl(),

  // Legacy compatibility
  baseURL: deriveApiBaseUrl(),

  // Cold-start tolerant default timeout (30s) for user-facing requests.
  TIMEOUT: 30000,
  timeout: 30000,
  retryAttempts: 3,
  RETRY: {
    MAX_ATTEMPTS: 3,
    DELAY: 1000,
    BACKOFF_MULTIPLIER: 2,
  },
  retryDelay: 1000,

  // Enterprise security settings
  withCredentials: true,
  SECURITY_HEADERS: {
    'X-Client-Version': '1.0.0',
    'X-Request-Source': 'legaltech-frontend',
    'X-Enterprise-Mode': 'true',
    'X-Agent-System': 'omniscient',
  },
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Version': '1.0.0',
    'X-Request-Source': 'legaltech-frontend',
    'X-Enterprise-Mode': 'true',
    'X-Agent-System': 'omniscient',
  },

  // Service discovery endpoints
  endpoints: {
    auth: '/api/auth',
    cases: '/api/cases',
    ai: '/api/ai',
    users: '/api/users',
    documents: '/api/documents',
    clients: '/api/clients',
    tasks: '/api/tasks',
    email: '/api/email',
    whiteboards: '/api/whiteboards',
    calendar: '/api/calendar',
    notifications: '/api/notifications',
    admin: '/api/admin',
    superAdmin: '/api/super-admin',
    analytics: '/api/analytics',
    performance: '/api/performance',
    security: '/api/security',
    audit: '/api/audit',
    health: '/api/health',
    system: '/api/system',
    dashboard: '/api/dashboard',
  },

  // Uppercase for consistency
  ENDPOINTS: {
    AUTH: '/auth',
    CASES: '/cases',
    AI_CHAT: '/ai/enhanced-chat',
    AI_CHAT_SIMPLE: '/ai/chat',
    AI_VOICE_COMMAND: '/ai/voice-command',
    USERS: '/users',
    DOCUMENTS: '/documents',
    DOCUMENTS_MEMOS: '/documents/memos',
    DOCUMENT_ANALYSIS: '/document-analysis',
    CLIENTS: '/clients',
    TASKS: '/tasks',
    EMAIL_ACCOUNTS: '/email/accounts',
    EMAIL_ROUTES: '/email',
    GMAIL: '/gmail',
    WHITEBOARDS: '/v1/whiteboards',
    CALENDAR: '/calendar',
    GOOGLE_CALENDAR: '/google-calendar',
    NOTIFICATIONS: '/notifications',
  },

  // AI Services endpoints
  aiEndpoints: {
    chat: '/api/ai/enhanced-chat',
    voiceCommand: '/api/ai/voice-command',
    chatSimple: '/api/ai/chat',
    documentAnalysis: '/api/ai/document-analysis',
    contractGeneration: '/api/ai/contract-generation',
    legalResearch: '/api/ai/legal-research',
  },

  // Document Management endpoints
  documentEndpoints: {
    documents: '/api/documents',
    memos: '/api/documents/memos',
    intelligence: '/api/document-intelligence',
    analysis: '/api/document-analysis',
    actions: '/api/document-actions',
    comparison: '/api/document-comparison',
    semanticSearch: '/api/document-semantic-search',
  },

  // Case Management endpoints
  caseEndpoints: {
    cases: '/api/cases',
    caseRoutes: '/api/case',
  },

  // Client Management endpoints
  clientEndpoints: {
    clients: '/api/clients',
    clientRoutes: '/api/client',
  },

  // Task Management endpoints
  taskEndpoints: {
    tasks: '/api/tasks',
    taskRoutes: '/api/task',
  },

  // Email Services endpoints
  emailEndpoints: {
    accounts: '/api/email/accounts',
    routes: '/api/email',
    gmail: '/api/gmail',
  },

  // Whiteboard Services endpoints
  whiteboardEndpoints: {
    whiteboards: '/api/v1/whiteboards',
    templates: '/api/v1/whiteboards/templates',
  },

  // Calendar Services endpoints
  calendarEndpoints: {
    calendar: '/api/calendar',
    googleCalendar: '/api/google-calendar',
  },

  // Authentication endpoints
  authEndpoints: {
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    refresh: '/api/auth/refresh-token',
    session: '/api/auth/session',
    me: '/api/auth/me',
    google: {
      url: '/api/auth/google/url',
      callback: '/api/auth/google/callback',
    },
    yahoo: {
      url: '/api/auth/yahoo/url',
      callback: '/api/auth/yahoo/callback',
    },
    microsoft: {
      url: '/api/auth/microsoft/url',
      callback: '/api/auth/microsoft/callback',
    },
    customEmail: {
      validate: '/api/auth/custom-email/validate',
    },
  },

  // Feature Flags
  features: {
    enhancedAI: true,
    voiceCommands: true,
    documentIntelligence: true,
    realTimeCollaboration: true,
    advancedAnalytics: true,
    semanticSearch: true,
    contractGeneration: true,
    legalResearch: true,
  },

  // Rate Limiting Configuration
  rateLimit: {
    requestsPerMinute: 60,
    burstLimit: 10,
    retryAfterHeader: 'Retry-After',
  },

  // Caching Configuration
  cache: {
    enabled: true,
    defaultTTL: 300000, // 5 minutes
    maxSize: 100,
    strategies: {
      documents: 600000, // 10 minutes
      cases: 300000, // 5 minutes
      users: 900000, // 15 minutes
      analytics: 1800000, // 30 minutes
    },
  },

  // Logging Configuration
  logging: {
    enabled: true,
    level: process.env.NODE_ENV === 'production' ? 'error' : 'debug',
    includeHeaders: process.env.NODE_ENV === 'development',
    includeBody: process.env.NODE_ENV === 'development',
    maxLogSize: 1024 * 1024, // 1MB
  },

  // Security Configuration
  security: {
    tokenExpiry: 3600000, // 1 hour
    refreshTokenExpiry: 2592000000, // 30 days
    maxLoginAttempts: 5,
    lockoutDuration: 900000, // 15 minutes
    passwordMinLength: 8,
    requireSpecialChars: true,
    sessionTimeout: 1800000, // 30 minutes
  },

  // Performance Configuration
  performance: {
    requestTimeout: 30000,
    uploadTimeout: 120000, // 2 minutes for file uploads
    downloadTimeout: 300000, // 5 minutes for file downloads
    maxConcurrentRequests: 10,
    requestQueueSize: 50,
  },

  // Error Handling Configuration
  errorHandling: {
    showUserFriendlyErrors: true,
    logTechnicalErrors: true,
    retryOnNetworkError: true,
    retryOnServerError: true,
    maxRetryAttempts: 3,
    retryDelay: 1000,
    exponentialBackoff: true,
  },
};

// Environment-specific configurations
export const ENV_CONFIG = {
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_TEST: process.env.NODE_ENV === 'test',

  // API URLs by environment
  API_URLS: {
    development: getApiBaseUrl(),
    production: process.env.REACT_APP_API_BASE_URL || getApiBaseUrl(),
    test: getApiBaseUrl(),
  },

  // Feature flags by environment
  FEATURES: {
    development: {
      enhancedLogging: true,
      mockResponses: false,
      debugMode: true,
    },
    production: {
      enhancedLogging: false,
      mockResponses: false,
      debugMode: false,
    },
    test: {
      enhancedLogging: true,
      mockResponses: true,
      debugMode: true,
    },
  },
};

// Calendar Configuration
export const CALENDAR_CONFIG = {
  // Google Calendar API
  GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,

  // Microsoft Calendar API
  MICROSOFT_CLIENT_ID: process.env.REACT_APP_MICROSOFT_CLIENT_ID,

  // Sync intervals (in milliseconds)
  SYNC_INTERVAL: 5 * 60 * 1000, // 5 minutes
  MAX_SYNC_ATTEMPTS: 3,
  SYNC_TIMEOUT: 120000, // 120 seconds to match main timeout
};

// Legacy support for backward compatibility
export const API_BASE_URL = API_CONFIG.baseURL;
